/**
 * Conversación multiturno del asistente.
 *
 * `step(estado, entrada, entorno)` recibe un mensaje (o un botón) y devuelve el estado
 * siguiente y, como mucho, un efecto que la interfaz ejecuta (aplicar una propuesta o
 * navegar). Es lo único que decide; no escribe datos. Las fases quedan separadas:
 *
 *   consulta   → respuesta calculada con datos reales (insights.ts), sin cambios.
 *   propuesta  → registro `pending` con elementos, explicación y advertencias.
 *   confirmación → solo por botón o un "sí" explícito (las destructivas exigen el verbo).
 *   ejecución  → efecto `apply`; la interfaz llama a applyAll().
 *   resultado  → recordResult() con lo que de verdad pasó (o el error).
 *
 * Contexto que se conserva entre turnos: los mensajes, las propuestas recientes, la
 * pregunta abierta y las referencias (último hábito, tarea y fecha mencionados) para
 * resolver "ese hábito" o "cambiemos el horario". No es memoria permanente: vive en el
 * dispositivo, caduca y se borra con la cuenta (ver store.ts).
 */
import { z } from 'zod';
import { addDays, isoWeekday, type LocalDate } from '@habitos/engine';
import type { PlanId } from '@habitos/protocol';
import type { Ctx } from '../domain/data';
import { currentVersion, currentWeekStartOf, type HabitRec } from '../domain/model';
import type { AppData } from '../domain/data';
import { makeT } from '../i18n/core';
import { matchHabits } from './deterministic';
import { plainI18n, summarizeProposal } from './describe';
import {
  agendaAnswer,
  dailyBrief,
  activeHabits,
  focusAnswer,
  habitProgress,
  habitsOverview,
  journalAnswer,
  neglectedHabits,
  notesAnswer,
  openTasks,
  statsSummary,
  tasksOverview,
  type Answer,
} from './insights';
import { intentSchema, MIN_CONFIDENCE, validateUnderstanding, type Changes, type Intent, type PreferenceChange, type Understander, type UnderstandContext } from './intents';
import { describePreference, preferredSlot, type Preferences } from './preferences';
import { DESTRUCTIVE, proposalSchema, type Proposal } from './interpreter';
import { ALL_DAYS, dayNames, Phrase, SLOT_DEFAULT, takeDaySet } from './normalize';
import { messageSchema, textRefSchema, tr, type Action, type Choice, type Message, type Part, type TextRef } from './parts';
import {
  alternativeGoal,
  buildGoalPlan,
  buildRoutinePlan,
  buildWeekPlan,
  draftSchema,
  relatedHabits,
  routineActivity,
  schedulableTasks,
  type Draft,
  type GoalDraft,
  type RoutineDraft,
  type WeekDraft,
} from './planner';
import { deterministicUnderstander, extractChanges, hasChanges, intentFor, parseAvailability } from './understand';
import { planRoom } from './writes';

export const MAX_MESSAGES = 60;
export const MAX_PROPOSALS = 12;

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Huella de una escritura (ver db/repo.ts): permite deshacer aunque se recargue la app. */
export const traceSchema = z.object({
  entity: z.string().max(32),
  id: z.string().max(64),
  created: z.boolean(),
  before: z.record(z.unknown()),
  after: z.record(z.unknown()),
});
export type Trace = z.infer<typeof traceSchema>;

export const proposalRecordSchema = z.object({
  id: z.string().max(64),
  revision: z.number().int().min(1),
  status: z.enum(['pending', 'applying', 'applied', 'cancelled', 'failed', 'interrupted', 'undone']),
  items: z.array(proposalSchema).max(30),
  draft: draftSchema,
  explain: z.array(textRefSchema).max(24),
  warnings: z.array(textRefSchema).max(12),
  result: textRefSchema.nullable(),
  /** Escrituras hechas al aplicar, para deshacer. null si no hay (o no se pueden deshacer así). */
  undo: z.array(traceSchema).max(400).nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type ProposalRecord = z.infer<typeof proposalRecordSchema>;

export const questionSchema = z.object({
  id: z.string().max(64),
  messageId: z.string().max(64),
  slot: z.enum(['time', 'timeAmbiguous', 'availability', 'activity', 'days', 'minutes', 'habit', 'task', 'change', 'choice', 'confirmIntent', 'destructive']),
  proposalId: z.string().max(64).nullable(),
  /** Intención a la que le falta un dato; se completa con la respuesta. */
  intent: intentSchema.nullable(),
});
export type Question = z.infer<typeof questionSchema>;

export const stateSchema = z.object({
  v: z.literal(1),
  id: z.string().max(64),
  owner: z.string().min(1).max(128),
  createdAt: z.number(),
  updatedAt: z.number(),
  messages: z.array(messageSchema).max(MAX_MESSAGES),
  proposals: z.array(proposalRecordSchema).max(MAX_PROPOSALS),
  activeProposalId: z.string().max(64).nullable(),
  question: questionSchema.nullable(),
  refs: z.object({ habitId: z.string().max(64).nullable(), taskId: z.string().max(64).nullable(), date: localDate.nullable() }),
  /** Último día en que se mostró el resumen del día. */
  briefDate: localDate.nullable().default(null),
});
export type ConversationState = z.infer<typeof stateSchema>;

export type Input =
  | { type: 'text'; text: string }
  | { type: 'choice'; messageId: string; choiceId: string }
  | { type: 'confirm'; proposalId: string }
  | { type: 'cancel'; proposalId: string }
  | { type: 'undo'; proposalId: string };

export type Effect =
  | { type: 'apply'; proposalId: string; items: Proposal[] }
  | { type: 'navigate'; to: string; agenda: boolean }
  /** Revertir lo aplicado por una propuesta (su huella o, si no la hay, el deshacer en memoria). */
  | { type: 'undo'; proposalId: string }
  /** Guardar una preferencia confirmada (la interfaz la guarda en el dispositivo). */
  | { type: 'savePrefs'; change: PreferenceChange };

export interface ConversationEnv {
  data: AppData;
  ctx: Ctx;
  locale: 'es' | 'en';
  now: number;
  newId: () => string;
  understander?: Understander;
  /** Preferencias recordadas (solo en el dispositivo); el planificador las usa como punto de partida. */
  prefs?: Preferences;
  /** Plan de la cuenta, para avisar antes de superar su límite. Sin él no se avisa (el límite se comprueba igual al guardar). */
  plan?: PlanId;
}

export interface StepResult {
  state: ConversationState;
  effect: Effect | null;
}

export function newConversation(owner: string, env: Pick<ConversationEnv, 'now' | 'newId'>): ConversationState {
  return { v: 1, id: env.newId(), owner, createdAt: env.now, updatedAt: env.now, messages: [], proposals: [], activeProposalId: null, question: null, refs: { habitId: null, taskId: null, date: null }, briefDate: null };
}

/** Lee un estado guardado; si no cumple el esquema se descarta (nunca se usa a medias). */
export function parseConversation(raw: unknown): ConversationState | null {
  const r = stateSchema.safeParse(raw);
  return r.success ? r.data : null;
}

// --- Turno: utilidades sobre una copia del estado -----------------------------------------------

const text = (key: string, params?: Record<string, string | number>): Part => ({ t: 'text', text: tr(key, params) });
const caption = (ref: TextRef): Part => ({ t: 'caption', text: ref });

class Turn {
  effect: Effect | null = null;
  readonly t: ReturnType<typeof makeT>;
  constructor(
    readonly s: ConversationState,
    readonly env: ConversationEnv,
  ) {
    this.t = makeT(env.locale);
  }
  say(kind: Message['kind'], parts: Part[], choices: Choice[] = []): Message {
    const m: Message = { id: this.env.newId(), role: 'assistant', at: this.env.now, kind, parts: parts.slice(0, 12), choices: choices.slice(0, 8) };
    this.s.messages.push(m);
    return m;
  }
  user(textValue: string): void {
    this.s.messages.push({ id: this.env.newId(), role: 'user', at: this.env.now, kind: 'user', text: textValue.slice(0, 500), parts: [], choices: [] });
  }
  ask(slot: Question['slot'], parts: Part[], choices: Choice[] = [], opts: { proposalId?: string | null; intent?: Intent | null } = {}): void {
    const m = this.say('question', parts, choices);
    this.s.question = { id: this.env.newId(), messageId: m.id, slot, proposalId: opts.proposalId ?? null, intent: opts.intent ?? null };
  }
  answer(a: Answer): void {
    this.say('answer', a.parts, a.choices ?? []);
    if (a.refs) this.s.refs = { ...this.s.refs, ...a.refs };
  }
  active(): ProposalRecord | null {
    return this.s.proposals.find((p) => p.id === this.s.activeProposalId && p.status === 'pending') ?? null;
  }
  choice(label: TextRef, action: Action): Choice {
    return { id: this.env.newId(), label, action };
  }
}

function clone(s: ConversationState): ConversationState {
  return structuredClone(s);
}

function finish(T: Turn): StepResult {
  const s = T.s;
  const rec = T.active();
  if (rec) refreshLimitNote(T, rec);
  s.updatedAt = T.env.now;
  if (s.messages.length > MAX_MESSAGES) s.messages = s.messages.slice(-MAX_MESSAGES);
  if (s.proposals.length > MAX_PROPOSALS) {
    const keep = new Set([s.activeProposalId, s.question?.proposalId].filter(Boolean));
    const removable = s.proposals.filter((p) => !keep.has(p.id) && p.status !== 'pending' && p.status !== 'applying');
    const drop = new Set(removable.slice(0, s.proposals.length - MAX_PROPOSALS).map((p) => p.id));
    s.proposals = s.proposals.filter((p) => !drop.has(p.id));
  }
  return { state: s, effect: T.effect };
}

function understandCtx(T: Turn): UnderstandContext {
  const { data, ctx, locale, now } = T.env;
  return {
    today: ctx.today,
    now,
    tz: ctx.tz,
    locale,
    weekStart: currentWeekStartOf(ctx.settings),
    habits: data.habits
      .filter((h) => !h.deletedAt)
      .map((h) => {
        const v = h.kind === 'task' ? null : currentVersion(h);
        return { id: h.id, name: h.name, kind: h.kind, goalType: (v?.goalType ?? 'binary') as 'binary' | 'numeric', unit: v?.unit ?? null, defaultIncrement: h.defaultIncrement, archived: Boolean(h.archivedAt), completed: Boolean(h.completedAt) };
      }),
    lists: data.notes.filter((n) => n.kind === 'list' && !n.deletedAt).map((n) => ({ id: n.id, title: n.title })),
    pending: T.active() ? 'proposal' : T.s.question ? 'question' : null,
  };
}

const asCtxHabit = (h: HabitRec) => ({ id: h.id, name: h.name, kind: h.kind, goalType: 'binary' as const, unit: null, defaultIncrement: 1, archived: Boolean(h.archivedAt), completed: Boolean(h.completedAt) });

/** Hábito (o tarea) al que se refiere el texto; si no nombra ninguno, el último mencionado. */
function resolve(T: Turn, textValue: string | null, pool: HabitRec[], ref: 'habitId' | 'taskId'): { one?: HabitRec; many?: HabitRec[] } {
  if (textValue) {
    const found = matchHabits(textValue, pool.map(asCtxHabit));
    if (found.length === 1) return { one: pool.find((h) => h.id === found[0]!.id)! };
    if (found.length > 1) return { many: found.map((f) => pool.find((h) => h.id === f.id)!) };
  }
  const id = T.s.refs[ref];
  const last = id ? pool.find((h) => h.id === id) : undefined;
  return last ? { one: last } : {};
}

// --- Entrada ------------------------------------------------------------------------------------

export async function step(state: ConversationState, input: Input, env: ConversationEnv): Promise<StepResult> {
  const T = new Turn(clone(state), env);
  switch (input.type) {
    case 'text': {
      const value = input.text.trim().replace(/\s+/g, ' ').slice(0, 500);
      if (!value) return { state, effect: null };
      T.user(value);
      await handleText(T, value);
      return finish(T);
    }
    case 'choice': {
      const msg = T.s.messages.find((m) => m.id === input.messageId);
      const c = msg?.choices.find((x) => x.id === input.choiceId);
      if (!msg || !c) return { state, effect: null };
      const live = T.s.question?.messageId === msg.id;
      // Las sugerencias (decir algo, abrir una pantalla) valen siempre; las respuestas a una
      // pregunta, solo mientras esa pregunta sigue abierta.
      if (!live && c.action.type !== 'say' && c.action.type !== 'navigate') {
        T.say('info', [text('chat.r.staleChoice')]);
        return finish(T);
      }
      T.user(T.t(c.label.key, c.label.params));
      await runAction(T, c.action, live ? T.s.question : null);
      return finish(T);
    }
    case 'confirm': {
      const rec = T.s.proposals.find((p) => p.id === input.proposalId);
      if (!rec || rec.status !== 'pending') {
        T.say('info', [text('chat.r.notPending')]);
        return finish(T);
      }
      T.user(T.t(rec.items.some((p) => DESTRUCTIVE.has(p.kind)) ? `assistant.confirm.${rec.items.find((p) => DESTRUCTIVE.has(p.kind))!.kind}` : 'assistant.confirm.default'));
      confirm(T, rec, true);
      return finish(T);
    }
    case 'cancel': {
      const rec = T.s.proposals.find((p) => p.id === input.proposalId);
      if (!rec || rec.status !== 'pending') return { state, effect: null };
      T.user(T.t('common.cancel'));
      cancel(T, rec);
      return finish(T);
    }
    case 'undo': {
      T.user(T.t('common.undo'));
      requestUndo(T, input.proposalId);
      return finish(T);
    }
  }
}

async function runAction(T: Turn, a: Action, q: Question | null): Promise<void> {
  switch (a.type) {
    case 'say':
      T.s.question = q && q.slot !== 'confirmIntent' ? T.s.question : null;
      return handleText(T, a.text);
    case 'intent':
      T.s.question = null;
      // Confirmación de "deshacer el último cambio": la pregunta guarda qué propuesta revertir.
      if (a.intent.type === 'undo_last' && q?.slot === 'confirmIntent' && q.proposalId) return requestUndo(T, q.proposalId);
      if (a.intent.type === 'remember_pref' && q?.slot === 'confirmIntent') {
        T.effect = { type: 'savePrefs', change: a.intent.change };
        return;
      }
      return handleIntent(T, a.intent, 1, '');
    case 'propose':
      T.s.question = null;
      return propose(T, a.items, { kind: 'items' });
    case 'changes': {
      T.s.question = null;
      const rec = q?.proposalId ? T.s.proposals.find((p) => p.id === q.proposalId && p.status === 'pending') : T.active();
      if (q?.intent) return handleIntent(T, mergeChanges(q.intent, a.changes), 1, '');
      if (!rec) return void T.say('info', [text('chat.r.notPending')]);
      return modify(T, rec, a.changes, '');
    }
    case 'navigate':
      T.effect = { type: 'navigate', to: a.to, agenda: Boolean(a.agenda) };
      return void T.say('info', [text('chat.r.opening')]);
    case 'rephrase':
      T.s.question = null;
      return void T.say('info', [text('chat.r.rephrase')]);
    case 'keepNew': {
      T.s.question = null;
      const rec = q?.proposalId ? (T.s.proposals.find((p) => p.id === q.proposalId && p.status === 'pending') ?? null) : T.active();
      if (!rec) return void T.say('info', [text('chat.r.notPending')]);
      return keepNew(T, rec, a.count);
    }
  }
}

function mergeChanges(intent: Intent, c: Changes): Intent {
  if ('changes' in intent) return { ...intent, changes: { ...intent.changes, ...c } } as Intent;
  if (intent.type === 'log_focus' && c.minutes) return { ...intent, minutes: c.minutes };
  return intent;
}

async function handleText(T: Turn, value: string): Promise<void> {
  const understander = T.env.understander ?? deterministicUnderstander;
  const uctx = understandCtx(T);
  const und = validateUnderstanding(await understander.understand(value, uctx));
  const from = T.s.messages.length;
  if (T.s.question && (await answerQuestion(T, T.s.question, value, und.intent))) return;
  await handleIntent(T, und.intent, und.confidence, value);
  // Transparencia: si lo interpretó la IA, se dice en la respuesta.
  if (und.via === 'ai') {
    const first = T.s.messages.slice(from).find((m) => m.role === 'assistant');
    if (first && first.parts.length < 12) first.parts.push(caption(tr('chat.r.viaAi')));
  }
}

// --- Respuestas a preguntas abiertas ----------------------------------------------------------

const ORDINALS: [RegExp, number][] = [
  [/^(?:la |el )?(?:1|primera?o?|uno|una)$/, 0],
  [/^(?:la |el )?(?:2|segund[oa]|dos)$/, 1],
  [/^(?:la |el )?(?:3|tercer[oa]?|tres)$/, 2],
  [/^(?:la |el )?(?:4|cuart[oa]|cuatro)$/, 3],
];

/** true si el texto respondía a la pregunta abierta. */
async function answerQuestion(T: Turn, q: Question, value: string, intent: Intent): Promise<boolean> {
  const n = new Phrase(value).norm.replace(/\s+/g, ' ').trim();
  const msg = T.s.messages.find((m) => m.id === q.messageId);
  if (intent.type === 'cancel' || intent.type === 'reset') return false;

  // "La segunda", "2": elige una opción de la pregunta.
  const ord = ORDINALS.find(([re]) => re.test(n));
  if (ord && msg?.choices[ord[1]]) {
    await runAction(T, msg.choices[ord[1]]!.action, q);
    return true;
  }
  const today = T.env.ctx.today as LocalDate;
  switch (q.slot) {
    case 'availability': {
      const av = parseAvailability(value);
      if (q.intent?.type === 'plan_routine') {
        const skip = /\b(?:da igual|cualquier|no se|sin preferencia|como quieras|elige tu|decide tu|elegi vos|decidi vos|lo que veas|cuando mejor encaje|cuando sea|cuando quieras)\b/.test(n);
        if (av.windows.length || skip) {
          T.s.question = null;
          planRoutine(T, { ...q.intent, windows: av.windows.length ? av.windows : null }, true);
          return true;
        }
      }
      if (av.windows.length) {
        T.s.question = null;
        buildWeek(T, { kind: 'week', windows: av.windows, assumedAllDays: av.assumedAllDays, removed: [], overrides: {}, variant: 0 }, av.daysWithoutRange);
        return true;
      }
      if (av.ambiguous) {
        T.ask('availability', [text('chat.q.rangeAmPm')], msg?.choices ?? []);
        return true;
      }
      if (intent.type === 'unknown' || intent.type === 'modify' || intent.type === 'confirm') {
        T.ask('availability', [text('chat.q.availabilityAgain')], msg?.choices ?? []);
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'time':
    case 'timeAmbiguous': {
      const c = extractChanges(new Phrase(value), today);
      if (q.slot === 'timeAmbiguous' && c.time === undefined) {
        const opts = msg?.choices ?? [];
        const pick = /\b(?:tarde|noche|pm)\b/.test(n) ? opts[1] : /\b(?:manana|madrugada|am)\b/.test(n) ? opts[0] : undefined;
        if (pick) {
          await runAction(T, pick.action, q);
          return true;
        }
      }
      if (c.time !== undefined || c.timeOptions || c.slot) {
        await runAction(T, { type: 'changes', changes: c }, q);
        return true;
      }
      if (intent.type === 'unknown') {
        T.ask(q.slot, [text('chat.q.timeAgain')], msg?.choices ?? [], { proposalId: q.proposalId, intent: q.intent });
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'days': {
      const ds = takeDaySet(new Phrase(value));
      if (ds && q.intent) {
        T.s.question = null;
        const c: Changes = ds.type === 'quota' ? { quota: ds.times } : { days: ds.days, except: ds.except.length ? ds.except : undefined };
        await handleIntent(T, mergeChanges(q.intent, c), 1, '');
        return true;
      }
      if (intent.type === 'unknown') {
        T.ask('days', [text('chat.q.daysAgain')], msg?.choices ?? [], { intent: q.intent });
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'minutes': {
      const c = extractChanges(new Phrase(value), today);
      if (c.minutes && q.intent) {
        T.s.question = null;
        await handleIntent(T, mergeChanges(q.intent, c), 1, '');
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'activity': {
      if (q.intent?.type === 'plan_goal' && (intent.type === 'unknown' || intent.type === 'plan_goal' || intent.type === 'command')) {
        const p = new Phrase(value);
        const c = extractChanges(p, today);
        const activity = p.rest();
        if (activity) {
          T.s.question = null;
          await handleIntent(T, { type: 'plan_goal', activity: activity[0]!.toLocaleUpperCase() + activity.slice(1, 80), changes: { ...q.intent.changes, ...c } }, 1, '');
          return true;
        }
      }
      T.s.question = null;
      return false;
    }
    case 'habit':
    case 'task': {
      if (!q.intent) return false;
      const pool = q.slot === 'habit' ? activeHabits(T.env.data) : openTasks(T.env.data);
      const found = matchHabits(value, pool.map(asCtxHabit));
      if (found.length === 1) {
        T.s.question = null;
        T.s.refs[q.slot === 'habit' ? 'habitId' : 'taskId'] = found[0]!.id;
        await handleIntent(T, withTarget(q.intent, found[0]!.name), 1, '');
        return true;
      }
      if (intent.type === 'unknown') {
        T.ask(q.slot, [text('chat.q.notFoundTarget', { name: value.slice(0, 60) })], msg?.choices ?? [], { intent: q.intent });
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'change': {
      if (!q.intent) return false;
      const c = extractChanges(new Phrase(value), today);
      if (hasChanges(c)) {
        T.s.question = null;
        await handleIntent(T, mergeChanges(q.intent, c), 1, '');
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'confirmIntent': {
      if (intent.type === 'confirm' && msg?.choices[0]) {
        await runAction(T, msg.choices[0].action, q);
        return true;
      }
      T.s.question = null;
      return false;
    }
    case 'destructive':
    case 'choice':
      T.s.question = null;
      return false;
  }
}

function withTarget(intent: Intent, name: string): Intent {
  if (intent.type === 'edit_habit' || intent.type === 'query_stats' || intent.type === 'log_focus') return { ...intent, habitText: name } as Intent;
  if (intent.type === 'reschedule_task') return { ...intent, taskText: name };
  return intent;
}

// --- Intenciones ---------------------------------------------------------------------------------

const CONTROL = new Set(['confirm', 'cancel', 'reset', 'greeting', 'help', 'thanks', 'unknown', 'modify', 'remove_item', 'alternative', 'undo_last', 'remember_pref', 'query_prefs']);
const EXAMPLE_KEYS = ['chat.examples.goal', 'chat.examples.week', 'chat.examples.neglected', 'chat.examples.agenda'] as const;

function exampleChoices(T: Turn): Choice[] {
  return EXAMPLE_KEYS.map((k) => T.choice(tr(k), { type: 'say', text: T.t(k) }));
}

async function handleIntent(T: Turn, intent: Intent, confidence: number, value: string): Promise<void> {
  const { data, ctx } = T.env;
  const today = ctx.today as LocalDate;

  if (confidence < MIN_CONFIDENCE && !CONTROL.has(intent.type) && intent.type !== 'command') {
    T.ask('confirmIntent', [text('chat.q.didYouMean', { what: T.t(`chat.intent.${intent.type}`) })], [
      T.choice(tr('chat.c.yes'), { type: 'intent', intent }),
      T.choice(tr('chat.c.no'), { type: 'rephrase' }),
    ]);
    return;
  }

  switch (intent.type) {
    case 'confirm': {
      const rec = T.active();
      if (!rec) return void T.say('info', [text('chat.r.nothingToConfirm')]);
      return confirm(T, rec, intent.explicit);
    }
    case 'cancel': {
      const rec = T.active();
      if (rec) return cancel(T, rec);
      if (T.s.question) {
        T.s.question = null;
        return void T.say('info', [text('chat.r.questionCancelled')]);
      }
      return void T.say('info', [text('chat.r.nothingToCancel')]);
    }
    case 'reset':
      return reset(T);
    case 'greeting':
      return void T.say('info', [text('chat.r.greeting')], exampleChoices(T));
    case 'thanks':
      return void T.say('info', [text('chat.r.thanks')]);
    case 'help':
      return void T.say(
        'info',
        [text('chat.r.helpIntro'), { t: 'list', title: null, items: ['plan', 'week', 'stats', 'agenda', 'edit', 'log', 'capture', 'safety'].map((k) => tr(`chat.help.${k}`)) }],
        exampleChoices(T),
      );
    case 'unknown': {
      const uctx = understandCtx(T);
      const options = intent.candidates.map((c) => ({ c, i: value ? intentFor(c, value, uctx) : null })).filter((x): x is { c: string; i: Intent } => x.i !== null);
      if (options.length) {
        T.ask('choice', [text('chat.q.whichIntent')], [...options.map((o) => T.choice(tr(`chat.intent.${o.c}`), { type: 'intent', intent: o.i })), T.choice(tr('chat.c.none'), { type: 'rephrase' })]);
        return;
      }
      return void T.say('info', [text('chat.r.unknown')], exampleChoices(T));
    }
    case 'command': {
      const it = intent.interpretation;
      if (it.type === 'unsupported') return void T.say('info', [text('chat.r.unknown')], exampleChoices(T));
      if (it.type === 'proposal') return propose(T, [it.proposal], { kind: 'items' });
      const i18n = plainI18n(T.env.locale);
      const choices = it.options.map((o) => T.choice(tr('chat.c.option', { summary: summarizeProposal(o.proposal, i18n, data) }), { type: 'propose', items: [o.proposal] }));
      if (choices.length) T.ask('choice', [text(it.question, it.params)], choices);
      else T.say('question', [text(it.question, it.params), caption(tr('assistant.rephrase'))]);
      return;
    }
    case 'query_habits':
      return T.answer(habitsOverview(T.env));
    case 'query_stats': {
      const q = T.env;
      if (intent.scope === 'neglected') return T.answer(neglectedHabits(q));
      const r = intent.scope === 'habit' ? resolve(T, intent.habitText, activeHabits(data), 'habitId') : {};
      if (r.one) return T.answer(habitProgress(q, r.one));
      if (r.many) return askTarget(T, 'habit', intent, r.many);
      return T.answer(statsSummary(q));
    }
    case 'query_tasks':
      return T.answer(tasksOverview(T.env));
    case 'query_agenda':
      return T.answer(agendaAnswer(T.env, intent.from as LocalDate, intent.to as LocalDate));
    case 'query_focus':
      return T.answer(focusAnswer(T.env));
    case 'query_notes':
      return T.answer(notesAnswer(T.env, intent.query));
    case 'query_journal':
      return T.answer(journalAnswer(T.env, intent.date as LocalDate | null));
    case 'plan_goal':
      return planGoal(T, intent);
    case 'plan_week': {
      if (!schedulableTasks(data, today).length) {
        T.say('answer', [text('chat.week.noTasks'), caption(tr('chat.week.noTasksDetail'))], [T.choice(tr('chat.c.createTask'), { type: 'say', text: T.t('chat.examples.task') }), T.choice(tr('chat.c.agendaWeek'), { type: 'say', text: T.t('chat.say.agendaWeek') })]);
        return;
      }
      if (intent.windows?.length) return buildWeek(T, { kind: 'week', windows: intent.windows, assumedAllDays: false, removed: [], overrides: {}, variant: 0 }, []);
      T.ask('availability', [text('chat.q.availability'), caption(tr('chat.q.availabilityHint', { count: schedulableTasks(data, today).length }))], [
        T.choice(tr('chat.c.availWeekdayEvenings'), { type: 'say', text: T.t('chat.say.availWeekdayEvenings') }),
        T.choice(tr('chat.c.availMornings'), { type: 'say', text: T.t('chat.say.availMornings') }),
        T.choice(tr('chat.c.availWeekend'), { type: 'say', text: T.t('chat.say.availWeekend') }),
      ]);
      return;
    }
    case 'plan_routine':
      return planRoutine(T, intent, false);
    case 'free_day':
      return freeDay(T, intent.days);
    case 'move_day':
      return moveDay(T, intent);
    case 'alternative':
      return alternative(T);
    case 'undo_last':
      return undoLast(T);
    case 'remember_pref':
      // Recordar algo de la persona también se confirma: nada se guarda sin su "sí".
      T.ask('confirmIntent', [text('chat.prefs.confirm', { what: describePreference(intent.change, T.t) })], [
        T.choice(tr('chat.c.savePref'), { type: 'intent', intent }),
        T.choice(tr('chat.c.undoNo'), { type: 'say', text: T.t('chat.say.undoNo') }),
      ]);
      return;
    case 'query_prefs': {
      const p = T.env.prefs;
      const lines = p ? listPreferences(p, T.t) : [];
      if (!lines.length) return void T.say('answer', [text('chat.prefs.none'), caption(tr('chat.prefs.howTo'))]);
      return void T.say('answer', [text('chat.prefs.list'), { t: 'list', title: null, items: lines.map((l) => tr('chat.prefs.item', { text: l })) }, caption(tr('chat.prefs.where'))]);
    }
    case 'edit_habit':
      return editHabit(T, intent);
    case 'reschedule_task':
      return rescheduleTaskIntent(T, intent);
    case 'log_focus': {
      if (!intent.minutes) {
        T.ask('minutes', [text('chat.q.focusMinutes')], [15, 25, 50].map((m) => T.choice(tr('focus.minutes', { count: m }), { type: 'changes', changes: { minutes: m } })), { intent });
        return;
      }
      if (intent.minutes > 180) return void T.say('question', [text('assistant.q.focusRange')]);
      const date = (intent.date ?? today) as LocalDate;
      if (date > today) return void T.say('question', [text('assistant.q.futureLog')]);
      const r = intent.habitText ? resolve(T, intent.habitText, activeHabits(data), 'habitId') : {};
      const h = r.one && intent.habitText && matchHabits(intent.habitText, [asCtxHabit(r.one)]).length ? r.one : undefined;
      return propose(T, [{ kind: 'log_focus', minutes: intent.minutes, habitId: h?.id ?? null, label: null, date }], { kind: 'items' });
    }
    case 'modify': {
      const rec = T.active();
      if (rec) return modify(T, rec, intent.changes, intent.text);
      // Sin propuesta abierta: si hay un hábito en contexto, es una edición de ese hábito.
      if (T.s.refs.habitId && activeHabits(data).some((h) => h.id === T.s.refs.habitId)) return editHabit(T, { type: 'edit_habit', habitText: null, changes: intent.changes });
      return void T.say('info', [text('chat.r.nothingToModify')], exampleChoices(T));
    }
    case 'remove_item': {
      const rec = T.active();
      if (rec?.draft.kind === 'week') {
        const items = rec.items.filter((p): p is Extract<Proposal, { kind: 'reschedule_task' }> => p.kind === 'reschedule_task');
        const found = matchHabits(intent.text, items.map((p) => ({ ...asCtxHabit({ id: p.habitId, name: p.habitName } as HabitRec) })));
        if (found.length === 1) {
          rec.draft.removed.push(found[0]!.id);
          return rebuildWeek(T, rec, tr('chat.p.removed', { name: found[0]!.name }));
        }
        return void T.say('question', [text('chat.r.removeNotFound')]);
      }
      // Fuera de un plan semanal, "elimina X" es una orden normal.
      const legacy = validateUnderstanding(await deterministicUnderstander.understand(intent.text, { ...understandCtx(T), pending: null }));
      return handleIntent(T, legacy.intent, legacy.confidence, intent.text);
    }
  }
}

function askTarget(T: Turn, slot: 'habit' | 'task', intent: Intent, pool: HabitRec[]): void {
  const choices = pool.slice(0, 6).map((h) => T.choice(tr('chat.c.target', { name: h.name }), { type: 'intent', intent: withTarget(intent, h.name) }));
  T.ask(slot, [text(pool.length ? (slot === 'habit' ? 'chat.q.whichHabit' : 'chat.q.whichTask') : slot === 'habit' ? 'chat.q.noHabits' : 'chat.q.noTasks')], choices, { intent });
}

function reset(T: Turn): void {
  const fresh = newConversation(T.s.owner, T.env);
  Object.assign(T.s, fresh);
  T.say('info', [text('chat.r.reset')]);
}

// --- Propuestas ---------------------------------------------------------------------------------

function propose(T: Turn, items: Proposal[], draft: Draft, explain: TextRef[] = [], warnings: TextRef[] = [], intro?: TextRef): void {
  const previous = T.active();
  if (previous) {
    previous.status = 'cancelled';
    previous.updatedAt = T.env.now;
  }
  const extra: TextRef[] = [...warnings];
  for (const p of items) {
    if (p.kind === 'create_task' && p.reminder) extra.push(tr('assistant.reminderNote'));
    if (p.kind === 'delete_habit') extra.push(tr('assistant.warnDelete', { name: p.habitName }));
    if (p.kind === 'archive_habit') extra.push(tr('assistant.warnArchive', { name: p.habitName }));
    if (p.kind === 'set_reminder') extra.push(tr('chat.plan.reminderNote'));
  }
  const rec: ProposalRecord = {
    id: T.env.newId(),
    revision: 1,
    status: 'pending',
    items,
    draft,
    explain,
    warnings: [...new Map(extra.map((w) => [JSON.stringify(w), w])).values()].slice(0, 12),
    result: null,
    undo: null,
    createdAt: T.env.now,
    updatedAt: T.env.now,
  };
  T.s.proposals.push(rec);
  T.s.activeProposalId = rec.id;
  T.s.question = null;
  for (const p of items) {
    if ('habitId' in p && p.habitId) T.s.refs[p.kind === 'reschedule_task' ? 'taskId' : 'habitId'] = p.habitId;
  }
  const parts: Part[] = [text(intro?.key ?? 'chat.p.intro', intro?.params), { t: 'proposal', id: rec.id }];
  if (previous) parts.push(caption(tr('chat.p.previousDiscarded')));
  T.say('proposal', parts);
  refreshLimitNote(T, rec);
  if (overLimit(T, rec)) offerAdjust(T, rec, 'chat.limit.adjust');
}

// --- Límite del plan ----------------------------------------------------------------------------

const isCreate = (p: Proposal): p is Extract<Proposal, { kind: 'create_habit' | 'create_task' }> => p.kind === 'create_habit' || p.kind === 'create_task';

function overLimit(T: Turn, rec: ProposalRecord): boolean {
  if (!T.env.plan) return false;
  const r = planRoom(rec.items, T.env.data, T.env.plan);
  return r.adds > r.left;
}

/** Aviso de cuántos hábitos añade la propuesta y cuántos espacios quedan. Se recalcula tras cada cambio. */
function limitNote(T: Turn, rec: ProposalRecord): TextRef | null {
  const plan = T.env.plan;
  if (!plan) return null;
  const r = planRoom(rec.items, T.env.data, plan);
  if (!r.adds) return null;
  if (r.adds > r.left) return r.left ? tr('chat.limit.over', { adds: r.adds, count: r.left, active: r.active, limit: r.limit }) : tr('chat.limit.full', { count: r.active, limit: r.limit });
  // En Premium el margen es amplio: solo se avisa al superarlo.
  if (plan !== 'free') return null;
  const after = r.left - r.adds;
  if (!after) return tr('chat.limit.last', { count: r.adds, limit: r.limit });
  return tr(`chat.limit.${rec.draft.kind === 'routine' ? 'routine' : 'proposal'}${r.adds === 1 ? 'One' : ''}`, { adds: r.adds, count: after });
}

function refreshLimitNote(T: Turn, rec: ProposalRecord): void {
  const note = limitNote(T, rec);
  const rest = rec.warnings.filter((w) => !w.key.startsWith('chat.limit.'));
  rec.warnings = (note ? [note, ...rest] : rest).slice(0, 12);
}

/** Ofrece ajustar la propuesta antes de guardar: quedarse con los que caben, archivar hábitos o ver los planes. */
function offerAdjust(T: Turn, rec: ProposalRecord, key: string): void {
  const r = planRoom(rec.items, T.env.data, T.env.plan!);
  const choices: Choice[] = [];
  if (r.left > 0) choices.push(T.choice(tr('chat.c.keepNew', { count: r.left }), { type: 'keepNew', count: r.left }));
  choices.push(T.choice(tr('chat.c.archiveSome'), { type: 'navigate', to: '/habits' }));
  choices.push(T.choice(tr('chat.c.seePlans'), { type: 'navigate', to: '/settings/plan' }));
  T.ask('choice', [text(key, { count: r.left, limit: r.limit }), caption(tr('chat.limit.nothingSaved'))], choices, { proposalId: rec.id });
}

/** "Quedarme solo con N": quita de la propuesta los hábitos nuevos que no caben (en una rutina, sus actividades). */
function keepNew(T: Turn, rec: ProposalRecord, count: number): void {
  const created = rec.items.filter(isCreate);
  if (created.length <= count) return void T.say('info', [text('chat.limit.alreadyFits')]);
  const dropped = created.slice(count);
  if (rec.draft.kind === 'routine') {
    const names = new Set(dropped.map((p) => p.name));
    rec.draft.activities = rec.draft.activities.filter((a) => !names.has(a.name));
    if (!rec.draft.activities.length) return cancel(T, rec, 'chat.limit.nothingLeft');
    const plan = buildRoutinePlan(rec.draft, T.env);
    Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings });
  } else {
    const drop = new Set<Proposal>(dropped);
    rec.items = rec.items.filter((p) => !drop.has(p));
    rec.draft = { kind: 'items' };
  }
  if (!rec.items.length) return cancel(T, rec, 'chat.limit.nothingLeft');
  rec.revision += 1;
  rec.updatedAt = T.env.now;
  T.s.question = null;
  refreshLimitNote(T, rec);
  T.say('proposal', [text('chat.p.updated', { revision: rec.revision }), { t: 'list', title: null, items: [tr('chat.limit.removed', { names: dropped.map((p) => p.name).join(', ') })] }, { t: 'proposal', id: rec.id }]);
}

function planGoal(T: Turn, intent: Extract<Intent, { type: 'plan_goal' }>): void {
  const c = intent.changes;
  if (!intent.activity) return T.ask('activity', [text('chat.q.activity')], [], { intent });
  // "Agrega lectura a mi rutina": si ya hay un hábito de esa actividad, se ajusta en vez de duplicarlo.
  const related = relatedHabits(intent.activity, activeHabits(T.env.data).filter((h) => h.kind === 'build'));
  if (related.length === 1 && !intent.forceNew && (c.minutes || c.weeklyMinutes) && !c.quota) {
    const h = related[0]!;
    const items = editItems(h, { ...c, days: c.days && c.days.length < 7 ? c.days : undefined });
    if (items.length) {
      T.s.refs.habitId = h.id;
      propose(T, items, { kind: 'items' }, [tr('chat.plan.adjustExisting', { name: h.name, activity: intent.activity }), tr('chat.plan.editFromToday')], [], tr('chat.p.introAdjust', { name: h.name }));
      T.say('info', [text('chat.p.orCreateNew')], [T.choice(tr('chat.c.createNew', { name: intent.activity }), { type: 'intent', intent: { ...intent, forceNew: true } })]);
      return;
    }
  }
  if (!c.days && !c.quota && !c.weeklyMinutes) {
    T.ask('days', [text('chat.q.days', { name: intent.activity })], [
      T.choice(tr('chat.c.daily'), { type: 'changes', changes: { days: ALL_DAYS } }),
      T.choice(tr('chat.c.weekdays'), { type: 'changes', changes: { days: [1, 2, 3, 4, 5] } }),
      T.choice(tr('chat.c.quota', { count: 3 }), { type: 'changes', changes: { quota: 3 } }),
    ], { intent });
    return;
  }
  // Preferencias recordadas: franja de la actividad y días libres, solo si el mensaje no dice otra cosa.
  const prefSlot = !c.time && !c.slot && !c.timeOptions ? preferredSlot(T.env.prefs, intent.activity) : null;
  const slot = c.slot ?? prefSlot ?? undefined;
  const draft: GoalDraft = {
    kind: 'goal',
    activity: intent.activity.trim(),
    minutes: c.minutes ?? null,
    days: c.quota || !c.days ? ALL_DAYS : c.days,
    except: c.quota ? [] : (c.except ?? []),
    quota: c.quota ?? null,
    time: c.time ?? (slot ? SLOT_DEFAULT[slot] : null),
    timeFromSlot: !c.time && Boolean(slot),
    weeklyMinutes: c.weeklyMinutes ?? null,
    freeDays: c.freeDays ?? (c.days ? [] : (T.env.prefs?.freeDays ?? [])),
    variant: 0,
  };
  const plan = buildGoalPlan(draft, T.env);
  if (prefSlot) plan.explain.unshift(tr('chat.prefs.usedSlot', { slot: T.t(`chat.prefs.slots.${prefSlot}`) }));
  propose(T, plan.items, draft, plan.explain, plan.warnings, tr('chat.p.introGoal'));
  const rec = T.active()!;
  // Una cuota flexible no aparece en días concretos de la agenda: se ofrece fijarlos.
  if (draft.quota !== null && !draft.weeklyMinutes) {
    const alt = alternativeGoal(draft, T.env);
    if (alt) T.say('info', [text('chat.p.quotaHint')], [T.choice(tr('chat.c.fixDays'), { type: 'say', text: T.t('chat.say.alternative') })]);
  }
  if (c.timeOptions) return askAmbiguous(T, rec, c.timeOptions);
  if (!draft.time) askTime(T, rec, 'chat.q.goalTime');
}

function askTime(T: Turn, rec: ProposalRecord, key: string): void {
  T.ask('time', [text(key)], [
    T.choice(tr('chat.c.morningAt', { time: SLOT_DEFAULT.morning }), { type: 'changes', changes: { time: SLOT_DEFAULT.morning } }),
    T.choice(tr('chat.c.afternoonAt', { time: SLOT_DEFAULT.afternoon }), { type: 'changes', changes: { time: SLOT_DEFAULT.afternoon } }),
    T.choice(tr('chat.c.eveningAt', { time: SLOT_DEFAULT.evening }), { type: 'changes', changes: { time: SLOT_DEFAULT.evening } }),
    ...(rec.draft.kind === 'goal' && rec.draft.time ? [T.choice(tr('chat.c.noTime'), { type: 'changes', changes: { time: null } } as Action)] : []),
  ], { proposalId: rec.id });
}

function askAmbiguous(T: Turn, rec: ProposalRecord | null, options: [string, string], intent: Intent | null = null): void {
  T.ask('timeAmbiguous', [text('chat.q.ambiguousTime', { a: options[0], b: options[1] })], options.map((o) => T.choice(tr('chat.c.at', { time: o }), { type: 'changes', changes: { time: o } })), {
    proposalId: rec?.id ?? null,
    intent,
  });
}

function buildWeek(T: Turn, draft: WeekDraft, daysWithoutRange: number[]): void {
  const plan = buildWeekPlan(draft, T.env);
  if (daysWithoutRange.length) plan.warnings.unshift(tr('chat.week.daysWithoutRange', { days: daysWithoutRange.map((d) => T.t(`chat.weekday.${d}`)).join(', ') }));
  if (!plan.items.length) {
    T.say('answer', [text('chat.week.nothingFits'), ...plan.warnings.map(caption)], [T.choice(tr('chat.c.otherAvailability'), { type: 'say', text: T.t('chat.examples.week') })]);
    return;
  }
  propose(T, plan.items, draft, plan.explain, plan.warnings, tr('chat.p.introWeek', { count: plan.items.length }));
}

function rebuildWeek(T: Turn, rec: ProposalRecord, change: TextRef): void {
  if (rec.draft.kind !== 'week') return;
  const plan = buildWeekPlan(rec.draft, T.env);
  if (!plan.items.length) {
    cancel(T, rec, 'chat.r.weekEmptyAfterChange');
    return;
  }
  Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings, revision: rec.revision + 1, updatedAt: T.env.now });
  T.say('proposal', [text('chat.p.updated', { revision: rec.revision }), { t: 'list', title: null, items: [change] }, { t: 'proposal', id: rec.id }]);
}

function editHabit(T: Turn, intent: Extract<Intent, { type: 'edit_habit' }>): void {
  const { data } = T.env;
  const pool = activeHabits(data);
  const r = resolve(T, intent.habitText, pool, 'habitId');
  if (r.many) return askTarget(T, 'habit', intent, r.many);
  if (!r.one) return askTarget(T, 'habit', intent, pool);
  const h = r.one;
  T.s.refs.habitId = h.id;
  const c = intent.changes;
  if (c.invalidTime) return void T.say('question', [text('assistant.q.invalidTime')]);
  if (c.timeOptions) return askAmbiguous(T, null, c.timeOptions, { ...intent, habitText: h.name });
  const items = editItems(h, c);
  if (!items.length) {
    if (c.askTime) {
      T.ask('time', [text('chat.q.reminderTime', { name: h.name })], [SLOT_DEFAULT.morning, SLOT_DEFAULT.afternoon, SLOT_DEFAULT.evening].map((time) => T.choice(tr('chat.c.at', { time }), { type: 'changes', changes: { time } })), {
        intent: { ...intent, habitText: h.name },
      });
      return;
    }
    T.ask('change', [text('chat.q.whatChange', { name: h.name })], [], { intent: { ...intent, habitText: h.name } });
    return;
  }
  propose(T, items, { kind: 'items' }, [tr('chat.plan.editFromToday')]);
}

/** Cambios sobre un hábito existente → update_habit y/o set_reminder. */
function editItems(h: HabitRec, c: Changes): Proposal[] {
  const v = currentVersion(h);
  const out: Proposal[] = [];
  const update: Extract<Proposal, { kind: 'update_habit' }> = { kind: 'update_habit', habitId: h.id, habitName: h.name };
  if (c.name) update.name = c.name;
  if (c.minutes) {
    if (v.unit === 'h') update.target = Math.round((c.minutes / 60) * 100) / 100;
    else if (v.unit === 'min') update.target = c.minutes;
    else if (v.goalType === 'binary' && h.kind === 'build') Object.assign(update, { goalType: 'numeric', target: c.minutes, unit: 'min' });
  }
  if (c.quota) update.schedule = { type: 'weekly_quota', mode: 'times', times: c.quota };
  else if (c.days) {
    const base = v.schedule.type === 'weekdays' ? v.schedule.days : v.schedule.type === 'daily' ? ALL_DAYS : [];
    const days = c.addDays ? [...new Set([...base, ...c.days])].sort((a, b) => a - b) : c.days;
    update.schedule = days.length === 7 ? { type: 'daily' } : { type: 'weekdays', days };
  }
  if (Object.keys(update).length > 3) out.push(update);
  const time = c.time ?? (c.slot ? SLOT_DEFAULT[c.slot] : undefined);
  if (time) {
    const sched = update.schedule ?? v.schedule;
    out.push({ kind: 'set_reminder', habitId: h.id, habitName: h.name, time, days: sched.type === 'weekdays' ? sched.days : null });
  }
  return out;
}

function rescheduleTaskIntent(T: Turn, intent: Extract<Intent, { type: 'reschedule_task' }>): void {
  const pool = openTasks(T.env.data);
  const r = resolve(T, intent.taskText, pool, 'taskId');
  if (r.many) return askTarget(T, 'task', intent, r.many);
  if (!r.one) return askTarget(T, 'task', intent, pool);
  const h = r.one;
  T.s.refs.taskId = h.id;
  const c = intent.changes;
  if (c.invalidDate) return void T.say('question', [text('assistant.q.invalidDate')]);
  if (c.timeOptions) return askAmbiguous(T, null, c.timeOptions, { ...intent, taskText: h.name });
  const date = c.date ?? (c.days?.length ? nextOn(T.env.ctx.today as LocalDate, c.days[0]!) : undefined);
  const time = c.time !== undefined ? c.time : c.slot ? SLOT_DEFAULT[c.slot] : undefined;
  if (date === undefined && time === undefined) {
    T.ask('change', [text('chat.q.whenTask', { name: h.name })], [
      T.choice(tr('chat.c.tomorrow'), { type: 'changes', changes: { date: addDays(T.env.ctx.today as LocalDate, 1) } }),
      T.choice(tr('chat.c.nextMonday'), { type: 'changes', changes: { date: nextOn(T.env.ctx.today as LocalDate, 1) } }),
    ], { intent: { ...intent, taskText: h.name } });
    return;
  }
  const dueDate = (date ?? h.dueDate ?? T.env.ctx.today) as LocalDate;
  if (dueDate < T.env.ctx.today) return void T.say('question', [text('assistant.q.pastDate')]);
  propose(T, [{ kind: 'reschedule_task', habitId: h.id, habitName: h.name, dueDate, dueTime: time === undefined ? (h.dueTime ?? null) : time }], { kind: 'items' });
}

const nextOn = (today: LocalDate, weekday: number): LocalDate => addDays(today, (weekday - isoWeekday(today) + 7) % 7 || 7);

/** Modifica la propuesta en curso: misma propuesta, nueva revisión. */
function modify(T: Turn, rec: ProposalRecord, c: Changes, value: string): void {
  if (c.invalidTime) return void T.say('question', [text('assistant.q.invalidTime')]);
  if (c.invalidDate) return void T.say('question', [text('assistant.q.invalidDate')]);
  if (c.timeOptions) return askAmbiguous(T, rec, c.timeOptions);
  const changed: TextRef[] = [];
  const time = c.time !== undefined ? c.time : c.slot ? SLOT_DEFAULT[c.slot] : undefined;

  if (rec.draft.kind === 'goal') {
    const d = rec.draft;
    if (time !== undefined) {
      d.time = time;
      d.timeFromSlot = c.time === undefined && Boolean(c.slot);
      changed.push(time ? tr('chat.p.changedTime', { time }) : tr('chat.p.changedNoTime'));
    }
    if (c.minutes) {
      d.minutes = c.minutes;
      changed.push(tr('chat.p.changedMinutes', { minutes: c.minutes }));
    }
    if (c.quota) {
      Object.assign(d, { quota: c.quota, days: ALL_DAYS, except: [] });
      changed.push(tr('chat.p.changedDays'));
    } else if (c.days) {
      d.days = c.addDays ? [...new Set([...d.days, ...c.days])].sort((a, b) => a - b) : c.days;
      d.except = c.addDays ? d.except.filter((x) => !c.days!.includes(x)) : (c.except ?? []);
      d.quota = null;
      changed.push(tr('chat.p.changedDays'));
    }
    if (c.name) {
      d.activity = c.name;
      changed.push(tr('chat.p.changedName', { name: c.name }));
    }
    if (c.weeklyMinutes) {
      d.weeklyMinutes = c.weeklyMinutes;
      d.quota = null;
      changed.push(tr('chat.p.changedWeekly', { minutes: c.weeklyMinutes }));
    }
    if (c.freeDays) {
      d.freeDays = [...new Set([...(d.freeDays ?? []), ...c.freeDays])].sort((a, b) => a - b);
      d.days = d.days.filter((x) => !c.freeDays!.includes(x));
      if (!d.days.length) d.days = ALL_DAYS.filter((x) => !d.freeDays.includes(x));
      changed.push(tr('chat.p.changedFree', { days: dayNames(c.freeDays, T.env.locale) }));
    }
    if (!changed.length) {
      if (c.askTime) return askTime(T, rec, 'chat.q.goalTime');
      return void T.say('question', [text('chat.r.noChange')]);
    }
    const plan = buildGoalPlan(d, T.env);
    Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings });
  } else if (rec.draft.kind === 'routine') {
    return modifyRoutine(T, rec, rec.draft, c, value, time);
  } else if (rec.draft.kind === 'week') {
    const d = rec.draft;
    if (c.freeDays) {
      d.windows = d.windows.map((w) => ({ ...w, days: w.days.filter((x) => !c.freeDays!.includes(x)) })).filter((w) => w.days.length);
      for (const [id, o] of Object.entries(d.overrides)) if (c.freeDays.includes(isoWeekday(o.date as LocalDate))) delete d.overrides[id];
      if (!d.windows.length) return cancel(T, rec, 'chat.r.weekEmptyAfterChange');
      return rebuildWeek(T, rec, tr('chat.p.changedFree', { days: dayNames(c.freeDays, T.env.locale) }));
    }
    const items = rec.items.filter((p): p is Extract<Proposal, { kind: 'reschedule_task' }> => p.kind === 'reschedule_task');
    const target = value ? matchHabits(value, items.map((p) => asCtxHabit({ id: p.habitId, name: p.habitName } as HabitRec))) : [];
    const av = value ? parseAvailability(value) : { windows: [], assumedAllDays: false };
    if (target.length === 1) {
      const item = items.find((p) => p.habitId === target[0]!.id)!;
      const date = c.date ?? (c.days?.length ? nextOn(T.env.ctx.today as LocalDate, c.days[0]!) : (item.dueDate ?? T.env.ctx.today));
      d.overrides[item.habitId] = { date, time: time === undefined ? item.dueTime : time };
      changed.push(tr('chat.p.moved', { name: item.habitName }));
    } else if (av.windows.length) {
      d.windows = av.windows;
      d.assumedAllDays = av.assumedAllDays;
      changed.push(tr('chat.p.changedAvailability'));
    } else {
      return void T.say('question', [text('chat.r.weekModifyHelp')]);
    }
    return rebuildWeek(T, rec, changed[0]!);
  } else {
    const next = rec.items.map((p) => modifyItem(p, c, time, T.env.ctx.today as LocalDate, changed));
    if (!changed.length) {
      if (c.askTime && rec.items.some((p) => p.kind === 'create_task' || p.kind === 'set_reminder' || p.kind === 'reschedule_task' || p.kind === 'create_habit')) return askTime(T, rec, 'chat.q.newTime');
      return void T.say('question', [text(rec.items.some((p) => MODIFIABLE.has(p.kind)) ? 'chat.r.noChange' : 'chat.r.cantModify')]);
    }
    rec.items = next;
  }
  rec.revision += 1;
  rec.updatedAt = T.env.now;
  T.s.question = null;
  T.say('proposal', [text('chat.p.updated', { revision: rec.revision }), { t: 'list', title: null, items: changed }, { t: 'proposal', id: rec.id }]);
}

const MODIFIABLE = new Set<Proposal['kind']>(['create_task', 'create_habit', 'log_activity', 'start_focus', 'log_focus', 'set_reminder', 'reschedule_task', 'update_habit']);

function modifyItem(p: Proposal, c: Changes, time: string | null | undefined, today: LocalDate, changed: TextRef[]): Proposal {
  const push = (ref: TextRef) => changed.push(ref);
  switch (p.kind) {
    case 'create_task': {
      const n = { ...p };
      if (c.date && c.date >= today) {
        n.dueDate = c.date;
        push(tr('chat.p.changedDate'));
      }
      if (time !== undefined) {
        n.dueTime = time;
        if (!time) n.reminder = false;
        if (time && !n.dueDate) n.dueDate = today;
        push(time ? tr('chat.p.changedTime', { time }) : tr('chat.p.changedNoTime'));
      }
      if (c.name) {
        n.name = c.name;
        push(tr('chat.p.changedName', { name: c.name }));
      }
      return n;
    }
    case 'create_habit': {
      const n = { ...p };
      if (c.quota) n.schedule = { type: 'weekly_quota', mode: 'times', times: c.quota };
      else if (c.days) {
        const base = p.schedule.type === 'weekdays' ? p.schedule.days : ALL_DAYS;
        const days = c.addDays ? [...new Set([...base, ...c.days])].sort((a, b) => a - b) : c.days;
        n.schedule = days.length === 7 ? { type: 'daily' } : { type: 'weekdays', days };
      }
      if (c.quota || c.days) push(tr('chat.p.changedDays'));
      if (c.minutes && p.habitKind === 'build') {
        Object.assign(n, { goalType: 'numeric', target: c.minutes, unit: 'min' });
        push(tr('chat.p.changedMinutes', { minutes: c.minutes }));
      }
      if (time !== undefined) {
        n.reminder = time ? { time, days: n.schedule.type === 'weekdays' ? n.schedule.days : null } : null;
        push(time ? tr('chat.p.changedTime', { time }) : tr('chat.p.changedNoTime'));
      } else if (n.reminder && n.schedule !== p.schedule) {
        n.reminder = { ...n.reminder, days: n.schedule.type === 'weekdays' ? n.schedule.days : null };
      }
      if (c.name) {
        n.name = c.name;
        push(tr('chat.p.changedName', { name: c.name }));
      }
      return n;
    }
    case 'log_activity': {
      const n = { ...p };
      const d = c.date ?? null;
      if (d && d <= today) {
        n.date = d;
        push(tr('chat.p.changedDate'));
      }
      if (c.minutes && p.amount !== null && p.unit === 'min') {
        n.amount = c.minutes;
        push(tr('chat.p.changedMinutes', { minutes: c.minutes }));
      }
      return n;
    }
    case 'start_focus':
    case 'log_focus': {
      if (!c.minutes || c.minutes > 180) return p;
      push(tr('chat.p.changedMinutes', { minutes: c.minutes }));
      return { ...p, minutes: c.minutes };
    }
    case 'set_reminder': {
      const n = { ...p };
      if (time) {
        n.time = time;
        push(tr('chat.p.changedTime', { time }));
      }
      if (c.days) {
        n.days = c.days.length === 7 ? null : c.days;
        push(tr('chat.p.changedDays'));
      }
      return n;
    }
    case 'reschedule_task': {
      const n = { ...p };
      if (c.date && c.date >= today) {
        n.dueDate = c.date;
        push(tr('chat.p.changedDate'));
      }
      if (time !== undefined) {
        n.dueTime = time;
        push(time ? tr('chat.p.changedTime', { time }) : tr('chat.p.changedNoTime'));
      }
      return n;
    }
    case 'update_habit': {
      const n = { ...p };
      if (c.minutes && n.target !== undefined) {
        n.target = c.minutes;
        push(tr('chat.p.changedMinutes', { minutes: c.minutes }));
      }
      if (c.name) {
        n.name = c.name;
        push(tr('chat.p.changedName', { name: c.name }));
      }
      return n;
    }
    default:
      return p;
  }
}

// --- Rutinas, días libres, mover días, alternativas y deshacer ---------------------------------------

/** Rutina con varias actividades. Pregunta la disponibilidad una vez (se puede omitir). */
function planRoutine(T: Turn, intent: Extract<Intent, { type: 'plan_routine' }>, asked: boolean): void {
  if (!intent.windows && !asked) {
    T.ask('availability', [text('chat.routine.askAvailability', { names: intent.activities.map((a) => a.name).join(', ') }), caption(tr('chat.routine.askHint'))], [
      T.choice(tr('chat.c.availWeekdayEvenings'), { type: 'say', text: T.t('chat.say.availWeekdayEvenings') }),
      T.choice(tr('chat.c.availMornings'), { type: 'say', text: T.t('chat.say.availMornings') }),
      T.choice(tr('chat.c.anyTime'), { type: 'say', text: T.t('chat.say.anyTime') }),
    ], { intent });
    return;
  }
  const draft: RoutineDraft = {
    kind: 'routine',
    activities: intent.activities.map((a) => routineActivity(a.name, a.changes, SLOT_DEFAULT)),
    freeDays: [...new Set([...intent.freeDays, ...(T.env.prefs?.freeDays ?? [])])].sort((a, b) => a - b),
    windows: intent.windows,
    variant: 0,
  };
  const plan = buildRoutinePlan(draft, T.env);
  if (!plan.items.length) return void T.say('answer', [text('chat.routine.nothing'), ...plan.warnings.map(caption)]);
  propose(T, plan.items, draft, plan.explain, plan.warnings, tr('chat.routine.intro', { count: intent.activities.length }));
}

function modifyRoutine(T: Turn, rec: ProposalRecord, d: RoutineDraft, c: Changes, value: string, time: string | null | undefined): void {
  const changed: TextRef[] = [];
  const named = value ? d.activities.filter((a) => relatedHabits(value, [{ id: a.name, name: a.name, kind: 'build' } as HabitRec]).length) : [];
  const targets = named.length ? named : d.activities.length === 1 ? d.activities : [];
  if (c.freeDays) {
    d.freeDays = [...new Set([...d.freeDays, ...c.freeDays])].sort((a, b) => a - b);
    for (const a of d.activities) if (a.days) a.days = a.days.filter((x) => !c.freeDays!.includes(x));
    for (const a of d.activities) if (a.days && !a.days.length) a.days = null;
    changed.push(tr('chat.p.changedFree', { days: dayNames(c.freeDays, T.env.locale) }));
  }
  const av = value ? parseAvailability(value) : { windows: [] };
  if (!targets.length && av.windows.length && !c.freeDays) {
    d.windows = av.windows;
    changed.push(tr('chat.p.changedAvailability'));
  }
  const perActivity = time !== undefined || c.minutes || c.days || c.quota || c.weeklyMinutes;
  if (perActivity && !targets.length && !changed.length) {
    return void T.say('question', [text('chat.routine.whichActivity', { names: d.activities.map((a) => a.name).join(', ') })]);
  }
  for (const a of targets) {
    if (time !== undefined) a.time = time;
    if (c.minutes) a.minutes = c.minutes;
    if (c.quota) Object.assign(a, { sessions: c.quota, days: null, weeklyMinutes: null });
    else if (c.days) a.days = c.days.filter((x) => !d.freeDays.includes(x));
    if (c.weeklyMinutes) Object.assign(a, { weeklyMinutes: c.weeklyMinutes, sessions: null });
    if (perActivity) changed.push(tr('chat.routine.changed', { name: a.name }));
  }
  if (!changed.length) return void T.say('question', [text('chat.routine.modifyHelp')]);
  const plan = buildRoutinePlan(d, T.env);
  if (!plan.items.length) return cancel(T, rec, 'chat.r.weekEmptyAfterChange');
  Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings, revision: rec.revision + 1, updatedAt: T.env.now });
  T.s.question = null;
  T.say('proposal', [text('chat.p.updated', { revision: rec.revision }), { t: 'list', title: null, items: changed }, { t: 'proposal', id: rec.id }]);
}

/** "Déjame libre el domingo": las tareas de ese día pasan al siguiente libre y los hábitos dejan de tocar ese día. */
function freeDay(T: Turn, days: number[]): void {
  const { data, ctx, locale } = T.env;
  const today = ctx.today as LocalDate;
  const items: Proposal[] = [];
  const explain: TextRef[] = [tr('chat.free.period', { days: dayNames(days, locale) })];
  const nextFree = (from: LocalDate): LocalDate => {
    let d = addDays(from, 1);
    while (days.includes(isoWeekday(d))) d = addDays(d, 1);
    return d;
  };
  for (const h of openTasks(data)) {
    if (!h.dueDate || h.dueDate < today || h.dueDate > addDays(today, 6) || !days.includes(isoWeekday(h.dueDate as LocalDate))) continue;
    const to = nextFree(h.dueDate as LocalDate);
    items.push({ kind: 'reschedule_task', habitId: h.id, habitName: h.name, dueDate: to, dueTime: h.dueTime ?? null });
  }
  const flexible: string[] = [];
  for (const h of activeHabits(data)) {
    if (h.kind === 'task') continue;
    const v = currentVersion(h);
    const sched = v.schedule;
    const current = sched.type === 'daily' ? ALL_DAYS : sched.type === 'weekdays' ? sched.days : null;
    if (!current) {
      if (sched.type === 'weekly_quota') flexible.push(h.name);
      continue;
    }
    if (!current.some((d) => days.includes(d))) continue;
    const next = current.filter((d) => !days.includes(d));
    if (!next.length) {
      explain.push(tr('chat.free.onlyThatDay', { name: h.name }));
      continue;
    }
    items.push(...editItems(h, { days: next }));
    const reminder = data.reminders.find((r) => r.habitId === h.id && !r.deletedAt && r.enabled);
    if (reminder && !items.some((p) => p.kind === 'set_reminder' && p.habitId === h.id)) {
      items.push({ kind: 'set_reminder', habitId: h.id, habitName: h.name, time: reminder.time, days: next });
    }
  }
  if (flexible.length) explain.push(tr('chat.free.flexible', { names: flexible.join(', ') }));
  if (!items.length) return void T.say('answer', [text('chat.free.alreadyFree', { days: dayNames(days, locale) }), ...explain.slice(1).map(caption)]);
  explain.push(tr('chat.plan.editFromToday'));
  propose(T, items, { kind: 'items' }, explain, [], tr('chat.free.intro', { days: dayNames(days, locale) }));
}

/** "Pasa el entrenamiento del miércoles al jueves": un hábito con días fijos o una tarea de ese día. */
function moveDay(T: Turn, intent: Extract<Intent, { type: 'move_day' }>): void {
  const { data, ctx, locale } = T.env;
  const today = ctx.today as LocalDate;
  const [fromName, toName] = [dayNames([intent.from], locale), dayNames([intent.to], locale)];
  const habits = activeHabits(data).filter((h) => h.kind !== 'task');
  const tasks = openTasks(data).filter((h) => h.dueDate && h.dueDate >= today && isoWeekday(h.dueDate as LocalDate) === intent.from);
  const named = relatedHabits(intent.targetText, habits)[0];
  const task = relatedHabits(intent.targetText, tasks)[0];
  // Una tarea de ese día nombrada en el mensaje gana a "el último hábito mencionado".
  const h = named ?? (task ? undefined : T.s.refs.habitId ? habits.find((x) => x.id === T.s.refs.habitId) : undefined);
  if (task && !named) {
    const target = addDays(task.dueDate as LocalDate, (intent.to - intent.from + 7) % 7);
    T.s.refs.taskId = task.id;
    return propose(T, [{ kind: 'reschedule_task', habitId: task.id, habitName: task.name, dueDate: target, dueTime: task.dueTime ?? null }], { kind: 'items' });
  }
  if (!h) return askTarget(T, 'habit', { type: 'edit_habit', habitText: null, changes: {} }, habits);
  T.s.refs.habitId = h.id;
  const sched = currentVersion(h).schedule;
  if (sched.type === 'weekly_quota') return void T.say('answer', [text('chat.move.flexible', { name: h.name })]);
  const current = sched.type === 'daily' ? ALL_DAYS : sched.type === 'weekdays' ? sched.days : null;
  if (!current) return void T.say('answer', [text('chat.move.unsupported', { name: h.name })]);
  if (!current.includes(intent.from)) return void T.say('answer', [text('chat.move.notThatDay', { name: h.name, day: fromName, days: dayNames(current, locale) })]);
  if (current.includes(intent.to)) {
    return void T.say('question', [text('chat.move.alreadyBoth', { name: h.name, from: fromName, to: toName })], [
      T.choice(tr('chat.c.removeDay', { day: fromName }), { type: 'intent', intent: { type: 'edit_habit', habitText: h.name, changes: { days: current.filter((d) => d !== intent.from) } } }),
    ]);
  }
  const next = [...current.filter((d) => d !== intent.from), intent.to].sort((a, b) => a - b);
  const items = editItems(h, { days: next });
  const reminder = data.reminders.find((r) => r.habitId === h.id && !r.deletedAt && r.enabled);
  if (reminder) items.push({ kind: 'set_reminder', habitId: h.id, habitName: h.name, time: reminder.time, days: next });
  propose(T, items, { kind: 'items' }, [tr('chat.move.explain', { name: h.name, from: fromName, to: toName, days: dayNames(next, locale) }), tr('chat.plan.editFromToday')]);
}

/** "Muéstrame otra alternativa": otra versión de la propuesta en curso (misma propuesta, nueva revisión). */
function alternative(T: Turn): void {
  const rec = T.active();
  if (!rec) return void T.say('info', [text('chat.alt.nothing')], exampleChoices(T));
  let change: TextRef;
  if (rec.draft.kind === 'goal') {
    const alt = alternativeGoal(rec.draft, T.env);
    if (!alt) return void T.say('answer', [text('chat.alt.none')]);
    rec.draft = alt.draft;
    const plan = buildGoalPlan(alt.draft, T.env);
    Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings });
    change = alt.change;
  } else if (rec.draft.kind === 'routine') {
    const before = JSON.stringify(rec.items);
    let plan = null;
    for (let i = 0; i < 6; i++) {
      rec.draft.variant += 1;
      plan = buildRoutinePlan(rec.draft, T.env);
      if (JSON.stringify(plan.items) !== before) break;
    }
    if (!plan || JSON.stringify(plan.items) === before) return void T.say('answer', [text('chat.alt.none')]);
    Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings });
    change = tr('chat.alt.routine');
  } else if (rec.draft.kind === 'week') {
    rec.draft.variant = (rec.draft.variant ?? 0) + 1;
    const plan = buildWeekPlan(rec.draft, T.env);
    if (!plan.items.length) return void T.say('answer', [text('chat.alt.none')]);
    Object.assign(rec, { items: plan.items, explain: plan.explain, warnings: plan.warnings });
    change = tr(rec.draft.variant % 2 ? 'chat.alt.weekLate' : 'chat.alt.weekEarly');
  } else {
    return void T.say('answer', [text('chat.alt.notForThis')]);
  }
  rec.revision += 1;
  rec.updatedAt = T.env.now;
  T.s.question = null;
  T.say('proposal', [text('chat.p.updated', { revision: rec.revision }), { t: 'list', title: null, items: [change] }, { t: 'proposal', id: rec.id }]);
}

/** "Cancela el último cambio": deshace lo último guardado desde el chat, tras confirmarlo. */
function undoLast(T: Turn): void {
  const pending = T.active();
  const last = [...T.s.proposals].reverse().find((p) => p.status === 'applied');
  if (!last) {
    // Sin nada guardado, "cancela" se refiere a la propuesta en curso.
    if (pending) return cancel(T, pending);
    return void T.say('info', [text('chat.undo.nothing')]);
  }
  const i18n = plainI18n(T.env.locale);
  const what = last.items.length === 1 ? summarizeProposal(last.items[0]!, i18n, T.env.data) : T.t('chat.p.planTitle', { count: last.items.length });
  T.ask('confirmIntent', [text('chat.undo.confirm', { what })], [
    T.choice(tr('chat.c.undoYes'), { type: 'intent', intent: { type: 'undo_last' } }),
    T.choice(tr('chat.c.undoNo'), { type: 'say', text: T.t('chat.say.undoNo') }),
  ]);
  // La confirmación llega como intención: se marca la pregunta para ejecutarla.
  T.s.question!.proposalId = last.id;
}

// --- Confirmación, cancelación y resultado ---------------------------------------------------------

function listPreferences(p: Preferences, t: (k: string, params?: Record<string, string | number>) => string): string[] {
  const out: string[] = [];
  if (p.earliest) out.push(describePreference({ kind: 'earliest', time: p.earliest }, t));
  if (p.latest) out.push(describePreference({ kind: 'latest', time: p.latest }, t));
  for (const v of Object.values(p.slots)) out.push(describePreference({ kind: 'slot', activity: v.label, slot: v.slot }, t));
  if (p.freeDays.length) out.push(describePreference({ kind: 'freeDays', days: p.freeDays }, t));
  return out;
}

/** Anota en la conversación que una preferencia se guardó (o no). */
export function recordPrefs(state: ConversationState, change: PreferenceChange, ok: boolean, env: Pick<ConversationEnv, 'now' | 'newId' | 'locale'>): ConversationState {
  const s = clone(state);
  const t = makeT(env.locale);
  s.messages.push({
    id: env.newId(),
    role: 'assistant',
    at: env.now,
    kind: ok ? 'result' : 'error',
    parts: ok ? [text('chat.prefs.saved', { what: describePreference(change, t) }), caption(tr('chat.prefs.where'))] : [text('chat.prefs.failed')],
    choices: [],
  });
  s.updatedAt = env.now;
  return s;
}

/** Pide a la interfaz que revierta lo aplicado por una propuesta. */
export function requestUndo(T: Turn, proposalId: string): void {
  const rec = T.s.proposals.find((p) => p.id === proposalId);
  if (!rec || rec.status !== 'applied') return void T.say('info', [text('chat.undo.nothing')]);
  T.effect = { type: 'undo', proposalId };
}

function confirm(T: Turn, rec: ProposalRecord, explicit: boolean): void {
  const destructive = rec.items.find((p) => DESTRUCTIVE.has(p.kind));
  if (destructive && !explicit) {
    T.ask('destructive', [text('chat.q.destructive', { verb: T.t(`assistant.confirm.${destructive.kind}`).toLocaleLowerCase(), name: 'habitName' in destructive ? destructive.habitName : '' })], [], { proposalId: rec.id });
    return;
  }
  // Los datos pudieron cambiar desde la propuesta (otra pestaña, sincronización).
  const missing = rec.items.find((p) => 'habitId' in p && p.habitId && !T.env.data.habits.some((h) => h.id === p.habitId && !h.deletedAt));
  if (missing) {
    rec.status = 'failed';
    rec.updatedAt = T.env.now;
    T.s.activeProposalId = null;
    T.say('error', [text('chat.r.staleProposal')]);
    return;
  }
  // No se intenta guardar lo que el plan rechazaría: se ofrece ajustarlo (el límite se vuelve a comprobar al escribir).
  if (overLimit(T, rec)) return offerAdjust(T, rec, 'chat.limit.blocked');
  rec.status = 'applying';
  rec.updatedAt = T.env.now;
  T.s.question = null;
  T.effect = { type: 'apply', proposalId: rec.id, items: rec.items };
}

function cancel(T: Turn, rec: ProposalRecord, key = 'chat.r.cancelled'): void {
  rec.status = 'cancelled';
  rec.updatedAt = T.env.now;
  T.s.activeProposalId = null;
  T.s.question = null;
  T.say('info', [text(key)]);
}

export type Outcome =
  | { ok: true; message: string; params?: Record<string, string | number>; undoable: boolean; writes?: Trace[] | null }
  | { ok: false; error: TextRef; retryable: boolean };

/** Anota lo que realmente pasó al aplicar. Si falló y se puede reintentar, la propuesta sigue pendiente. */
export function recordResult(state: ConversationState, proposalId: string, outcome: Outcome, env: Pick<ConversationEnv, 'now' | 'newId' | 'locale'>): ConversationState {
  const s = clone(state);
  const rec = s.proposals.find((p) => p.id === proposalId);
  if (!rec) return s;
  rec.updatedAt = env.now;
  const msg = (kind: Message['kind'], parts: Part[]) => s.messages.push({ id: env.newId(), role: 'assistant', at: env.now, kind, parts, choices: [] });
  if (outcome.ok) {
    rec.status = 'applied';
    rec.result = tr(outcome.message, outcome.params ?? {});
    rec.undo = outcome.undoable && outcome.writes?.length ? outcome.writes.slice(0, 400) : null;
    s.activeProposalId = s.activeProposalId === rec.id ? null : s.activeProposalId;
    msg('result', [{ t: 'text', text: rec.result }, ...(outcome.undoable ? [caption(tr('chat.r.undoHint'))] : [])]);
  } else {
    rec.status = outcome.retryable ? 'pending' : 'failed';
    if (!outcome.retryable && s.activeProposalId === rec.id) s.activeProposalId = null;
    msg('error', [{ t: 'text', text: outcome.error }, caption(tr(outcome.retryable ? 'chat.r.nothingSavedRetry' : 'chat.r.nothingSaved'))]);
  }
  s.updatedAt = env.now;
  if (s.messages.length > MAX_MESSAGES) s.messages = s.messages.slice(-MAX_MESSAGES);
  return s;
}

export function recordUndo(state: ConversationState, proposalId: string, ok: boolean, env: Pick<ConversationEnv, 'now' | 'newId'>, conflicts = 0): ConversationState {
  const s = clone(state);
  const rec = s.proposals.find((p) => p.id === proposalId);
  if (rec && ok) {
    rec.status = 'undone';
    rec.undo = null;
  }
  const parts = ok ? [text(conflicts ? 'chat.undo.partial' : 'assistant.undone', { count: conflicts })] : [text('chat.r.undoFailed')];
  s.messages.push({ id: env.newId(), role: 'assistant', at: env.now, kind: ok ? 'result' : 'error', parts, choices: [] });
  s.updatedAt = env.now;
  return s;
}

/**
 * Al recuperar una conversación: una propuesta que quedó "aplicándose" (se cerró la app a
 * mitad) no se repite ni se da por hecha; se marca como interrumpida y se avisa.
 */
export function recoverConversation(state: ConversationState, env: Pick<ConversationEnv, 'now' | 'newId'>): ConversationState {
  if (!state.proposals.some((p) => p.status === 'applying')) return state;
  const s = clone(state);
  for (const p of s.proposals) {
    if (p.status !== 'applying') continue;
    p.status = 'interrupted';
    if (s.activeProposalId === p.id) s.activeProposalId = null;
  }
  s.messages.push({ id: env.newId(), role: 'assistant', at: env.now, kind: 'error', parts: [text('chat.r.interrupted')], choices: [] });
  return s;
}

/** Añade el resumen del día una vez al día (al abrir el chat). No toca los datos. */
export function withDailyBrief(state: ConversationState, env: ConversationEnv): ConversationState {
  if (state.briefDate === env.ctx.today) return state;
  const s = clone(state);
  const a = dailyBrief(env);
  s.messages.push({ id: env.newId(), role: 'assistant', at: env.now, kind: 'answer', parts: a.parts.slice(0, 12), choices: (a.choices ?? []).map((c) => ({ ...c, id: env.newId() })) });
  if (s.messages.length > MAX_MESSAGES) s.messages = s.messages.slice(-MAX_MESSAGES);
  s.briefDate = env.ctx.today;
  s.updatedAt = env.now;
  return s;
}

/** Propuesta pendiente que la interfaz debe mostrar con botones. */
export function pendingProposal(s: ConversationState): ProposalRecord | null {
  return s.proposals.find((p) => p.id === s.activeProposalId && p.status === 'pending') ?? null;
}
